import React from 'react';
import {
  Box, Button, IconButton, Stack, Typography,
} from '@mui/material';
import { Trans } from '@gamiphy/react-i18n';
import { observer } from 'mobx-react';
import { AppCashbackStore } from '@gamiphy/loyalty-station-desktop/store/appCashback';
import profits from '@gamiphy/loyalty-station-desktop/components/cashback/assets/profits.svg';
import { AppStore } from '@gamiphy/loyalty-station-desktop/store/app';
import { ThemeStore } from '@gamiphy/loyalty-station-desktop/store/theme';
import { UserStore } from '@gamiphy/loyalty-station-desktop/store/user';
import Close from '@mui/icons-material/Close';
import { CashbackPopupsStore } from '@gamiphy/loyalty-station-desktop/store/cashbackPopups';
import { formatCashbackPromotionValue } from '../../../utils';
import Verified from '../loggedIn/Verified';

const Cashback: React.FC<{
  onSignupClick: () => void,
}> = ({
  onSignupClick,
}) => {
  const { title, description } = React.useMemo<{ title: React.ReactNode, description: React.ReactNode }>(() => {
    if (AppStore.app.agent === 'wake') {
      return {
        title: <Trans
          i18nKey="cashbackPopup.cashbackAwaits"
          defaults="Congrats!<br />{{cashback}} cashback awaits you!"
          values={{
            cashback: AppCashbackStore.highestPromotionAppCashback && formatCashbackPromotionValue(AppCashbackStore.highestPromotionAppCashback),
          }}
        />,
        description: (
          <>
            <Trans
              i18nKey="cashbackPopup.predefinedPromotions.becomeMember"
              defaults="Become a member today and earn cashback on your orders"
            />
            {AppCashbackStore.appCashbacks?.map((appCashback, index, appCashbacks) => (
              <Box component="p" my={0.5} key={appCashback.id}>
                {index === (appCashbacks.length - 1) ? (
                  <Trans
                    i18nKey="coreloyalty.cashback.predefinedPromotion.promotionDetails.last"
                    defaults="{{value}} for orders over {{rangeStart, currency}}"
                    values={{
                      value: formatCashbackPromotionValue(appCashback),
                      rangeStart: appCashback.minPurchaseValue,
                    }}
                  />
                ) : (
                  <Trans
                    i18nKey="coreloyalty.cashback.predefinedPromotion.promotionDetails"
                    defaults="{{value}} for orders between {{rangeStart, currency}} - {{rangeEnd. currency}}"
                    values={{
                      value: formatCashbackPromotionValue(appCashback),
                      rangeStart: appCashback.minPurchaseValue,
                      rangeEnd: appCashbacks[index + 1]?.minPurchaseValue,
                    }}
                  />
                )}
              </Box>
            ))}
          </>
        ),
      };
    }

    return {
      title: <Trans
        i18nKey="cashbackPopup.cashbackAwaits"
        defaults="Congrats!<br />{{cashback}} cashback awaits you!"
        values={{
          cashback: `${AppCashbackStore.defaultAppCashback?.percentage}%`,
        }}
      />,
      description: <Trans
        i18nKey="cashbackPopup.becomeMember"
        defaults="Become a member today and unlock {{cashbackPercentage}}% cashback on your first order when you spend over {{purchaseValue, currency}}."
        values={{
          purchaseValue: AppCashbackStore.defaultAppCashback?.minPurchaseValue,
          cashbackPercentage: AppCashbackStore.defaultAppCashback?.percentage,
        }}
      />,
    };
  }, [AppCashbackStore.defaultAppCashback,
    AppStore.app.agent,
    AppCashbackStore.highestPromotionAppCashback,
  ]);

  if (UserStore.user?.phone) return <Verified />;

  return (
    <Box p={2} maxWidth={350}>
      <Box pb="1.6rem" display="flex" justifyContent="flex-end">
        <IconButton sx={{ ml: 'auto', p: 0 }} disableRipple onClick={() => CashbackPopupsStore.closeCashbackPopup()}>
          <Close fontSize="large" />
        </IconButton>
      </Box>
      <Stack alignItems="center" spacing={1} textAlign="center">
        <Box component="img" src={ThemeStore.theme.popupsConfig?.welcomeGift?.image ?? profits} width={96} />
        <Typography fontSize={20} fontWeight={700}>
          {title}
        </Typography>
        <Typography
          fontSize={14}
          fontWeight={500}
          sx={{
            opacity: 0.5,
            mb: 1,
          }}
        >
          {description}
        </Typography>
        <Button
          variant="contained"
          fullWidth
          onClick={onSignupClick}
          sx={{
            fontSize: 16,
          }}
        >
          <Trans
            i18nKey="shared.signUp"
            defaults="Sign Up"
          />
        </Button>
      </Stack>
    </Box>
  );
};

export default observer(Cashback);
